import {ColumnValueBodyStyle, IDatasetTableCommonProps,} from "components/dashboard/dataset-table/utils/interfaces";
import {
  calculateColSpan,
  CombinedColumnRows,
  findHighlightRange,
  StyledRows,
  WIDTH_LOOKUP_TABLE
} from "components/dashboard/dataset-table/utils/utils";
import {TitleOverrideAndDescriptionTemplate} from "components/templates/DataTableCellTemplates";
import {DatasetDefaultConfig} from "models/dataset-models";
import {Column} from "primereact/column";
import {ColumnGroup} from "primereact/columngroup";
import {DataTable} from "primereact/datatable";
import {Row} from "primereact/row";
import {I, T} from "services/lang/LanguageService";
import React, {useState} from "react";
import {Tooltip} from "react-tooltip";
import {ChronoUnitLegacy, dateToReadableString} from "utils/time-utils";
import {DatasetStringValueMapperDTO} from "../charts/interfaces";
import ColumnTabWithMapper from "./ColumnTabWithMapper";
import {formatNumberWithGaps} from "../product-template-table/utils/utils";
import DatasetChartButtonWrapper, {TableTypes} from "./DatasetChartButtonWrapper";

interface IDataset {
  mapper?: DatasetStringValueMapperDTO;
  children?: IDataset[];
  stringValueMapper: number;
}

const DatasetTableGrouped = ({
  config,
  data,
  datasetsArray,
  totals,
  setIsDialogOpen,
  formatNumbers,
}: IDatasetTableCommonProps & { datasetsArray: IDataset[] }) => {
  const [currentIdentifiers, setCurrentIdentifiers] = useState<string[]>([]);

  const createHeaderGroup = (datasets: any[], level = 0): any[] => {
    const rows: JSX.Element[] = [];
    const rowElements: JSX.Element[] = [];

    datasets.forEach((dataset, index) => {
      if (dataset.header) {
        const colSpan = calculateColSpan(dataset.children);
        rowElements.push(
          <Column
            key={`${dataset.header}-${level}`}
            align="center"
            colSpan={colSpan}
            header={<div className="white-space-normal">{I(dataset.header)}</div>}
          />
        );
      }

      if (dataset.children) {
        rows.push(...createHeaderGroup(dataset.children, level + 1));
      } else {
        rowElements.push(
          <Column
            key={dataset.identifier}
            align="center"
            field={dataset.identifier}
            header={
              <>
                {" "}
                {TitleOverrideAndDescriptionTemplate(
                  {
                    description: dataset.description,
                    title: dataset.title,
                    overriddenTitle: dataset.overriddenTitle,
                    unit: dataset.unit,
                    identifier: dataset.identifier,
                  },
                  3
                )}                
                  <DatasetChartButtonWrapper
                     identifier={dataset.identifier}
                     currentIdentifiers={currentIdentifiers}
                     setCurrentIdentifiers={setCurrentIdentifiers}
                     setIsDialogOpen={setIsDialogOpen}
                     tableType={TableTypes.GROUPED}
                  />                
              </>
            }
          />
        );
      }

      if (index === datasets.length - 1 && rowElements.length > 0) {
        if (level === 0 && !config?.hideDateColumn) {
          rowElements.unshift(
            <Column
              key="date"
              header={T("word.date")}
              align="center"
              style={{
                width: WIDTH_LOOKUP_TABLE[config?.dateFormat || ChronoUnitLegacy.MINUTES],
              }}
            />
          );
        }
        rows.unshift(<Row key={`row-${level}`}>{rowElements}</Row>);
      }
    });

    return rows;
  };

  const rows = createHeaderGroup(datasetsArray);

  const groupedRows: { [key: string]: React.ReactElement } = {};
  for (let i = 0; i < rows.length; i++) {
    const { key } = rows[i];
    if (groupedRows[key]) {
      groupedRows[key] = CombinedColumnRows(groupedRows[key], rows[i]);
    } else {
      groupedRows[key] = rows[i];
    }
  }

  const CombinedAndStyledRows = StyledRows(Object.values(groupedRows));

  const headerGroup = <ColumnGroup>{CombinedAndStyledRows}</ColumnGroup>;

  const extractDatasets = (datasets: any[]): any[] => {
    let extracted: DatasetDefaultConfig[] = [];
    for (const dataset of datasets) {
      if (dataset.children) {
        extracted = [...extracted, ...extractDatasets(dataset.children)];
      } else {
        extracted.push(dataset);
      }
    }
    return extracted;
  };

  const extractedDatasets = extractDatasets(datasetsArray);

  const columnValueBodyTemplate = (rowData: any, column: any) => {
    const value = rowData[column.field];
    const correspondingDataset = extractedDatasets.find(d => d.identifier === column.field);
    const valueDecimalPlaces = correspondingDataset?.decimalPlaces;
    const defaultHighlight = correspondingDataset?.defaultHighlight;
    let style: ColumnValueBodyStyle = { padding: "0.25rem" };

    if (correspondingDataset && typeof value === "number") {
      const highlightRange = findHighlightRange(value, correspondingDataset.highlightRanges || []);
      style = highlightRange
        ? {
            color: highlightRange.color,
            backgroundColor: highlightRange.bgColor,
            padding: "0.25rem",
          }
        : {
            padding: "0.25rem",
            color: defaultHighlight?.color ?? "#000000",
            backgroundColor: defaultHighlight?.bgColor ?? "#FFFFFF",
          };
    }

    return {
      style,
      content: (
        <div style={style}>
          {correspondingDataset.stringValueMapper && (
            <ColumnTabWithMapper value={value} mapperId={correspondingDataset.stringValueMapper} />
          )}
          {!correspondingDataset.stringValueMapper && typeof value === "number" && formatNumbers
            ? formatNumberWithGaps(value.toFixed(valueDecimalPlaces))
            : value}
          {!value && value !== 0 && "-"}
        </div>
      ),
    };
  };

  const createColumns = (datasets: any[]): any[] => {
    return datasets.flatMap(dataset => {
      if (dataset.children) {
        return createColumns(dataset.children);
      }
      return (
        <Column
          key={dataset.identifier}
          align="center"
          field={dataset.identifier}
          body={(rowData: any, column: any) => {
            const { style, content } = columnValueBodyTemplate(rowData, column);
            return (
              <div className="w-full" style={style}>
                {content}
              </div>
            );
          }}
        />
      );
    });
  };

  const columns = createColumns(datasetsArray);

  const footerGroup = () => {
    if (config?.showTotal && totals.length > 0) {
      return (
        <ColumnGroup>
          <Row>
            {!config?.hideDateColumn && (
              <Column
                style={{
                  width: WIDTH_LOOKUP_TABLE[config?.dateFormat || ChronoUnitLegacy.MINUTES],
                }}
              />
            )}
            {totals.map(dataset => {
              return (
                <Column
                  key={dataset.identifier}
                  align="center"
                  footer={
                    <div
                      data-tooltip-id="footer-total-tooltip"
                      data-tooltip-content={I(dataset.tooltip || "")}
                      data-tooltip-place="bottom"
                    >
                      {dataset.value}
                    </div>
                  }
                />
              );
            })}
          </Row>
        </ColumnGroup>
      );
    }
  };

  const dateColumnStyle = {
    width: WIDTH_LOOKUP_TABLE[config?.dateFormat || ChronoUnitLegacy.MINUTES],
    minWidth: WIDTH_LOOKUP_TABLE[config?.dateFormat || ChronoUnitLegacy.MINUTES],
    maxWidth: WIDTH_LOOKUP_TABLE[config?.dateFormat || ChronoUnitLegacy.MINUTES],
  };

  const dateBodyTemplate = (rowData: { date: string | number | Date }): string => {
    const dateDate = new Date(rowData.date);
    const dateFormat = config?.dateFormat || ChronoUnitLegacy.MINUTES;
    return dateToReadableString(dateDate, dateFormat as ChronoUnitLegacy);
  };

  return (
    <>
      <Tooltip id="footer-total-tooltip" opacity={0.7} place="top" float className="z-5" />
      <DataTable
        value={data || []}
        size="small"
        headerColumnGroup={headerGroup}
        footerColumnGroup={footerGroup()}
        scrollable
        scrollDirection="horizontal"
        width="100%"
        className="table-with-borders"
      >
        {!config?.hideDateColumn && (
          <Column
            field="date"
            header={T("word.date")}
            align="center"
            body={dateBodyTemplate}
            style={dateColumnStyle}
          />
        )}
        {columns}
      </DataTable>
    </>
  );
};

export default DatasetTableGrouped;
